import * as React from 'react';
import { Icon } from '../icon/Icon';
import { mcCheck, mcSave, mcSettings, mcShowFooter } from '@mc/react-ui-icons';
import { IData } from '../virtual-data/VirtualData';
import {
  FinalTableFilterData,
  FinalTableProfileData,
  FinalTableProfiles,
  FinalTableProfileType,
  FinalTableProps,
  FinalTableSettings,
  FinalTableViews,
} from './FinalTableProps';
import { Menu } from '../menu/Menu';
import { MenuItem } from '../menu/MenuItem';
import { Button } from '../button/Button';
import { MenuDivider } from '../menu/MenuDivider';
import { TableColumnsModal } from './TableColumnsModal';
import { useCallback } from 'react';
import { SaveByNameModalForm } from '../filter/SaveByNameModalForm';
import { TableSettingsEnumType } from '../filter/types';
import {
  showConfirm,
  showDeleteConfirm,
  showInfo,
} from '../modal/modalFunctions';
// @ts-ignore
import { v4 as uuidv4 } from 'uuid';
import { useNastaveni } from '@mc/react-app-base';
import { isEqual } from 'lodash-es';
import { Tag } from '../tag/Tag';
import {
  getColumnFieldNoCheck,
  getColumnsView,
  getVisibleColumns,
  supportsAggregate,
} from './utils';
import { RowsDataConfig } from '../rows-data/useRowsData';
import { Modal } from '../modal/Modal';

export type Props<DATA extends IData<ID>, FILTER, ID> = {
  finalTableProps: FinalTableProps<DATA, FILTER, ID>;
  /**
   * Klíč / Identifikace pro ukládání nastavení (např. filtry, profily apod.).
   */
  settingsKey?: string;
};

export const GLOBAL_SAVED_SETTINGS_KEY = 'globalSettings.table.settings';
export const GLOBAL_SAVED_PROFILES_KEY = 'globalSettings.profiles';
export const GLOBAL_SAVED_VIEWS_KEY = 'globalSettings.views';

export const getInitialTableConfig = (
  profile: FinalTableProfileData
): Partial<RowsDataConfig> => {
  const result: Partial<RowsDataConfig> = {};

  result.initialColumns = profile.columns;
  result.initialAggregate = profile.aggregate;
  result.initialGrouping = profile.grouping;
  result.initialSort = profile.sort;
  result.initialShowFooter = profile.showFooter;
  result.initialGroupColumnWidth = profile.groupColumnWidth;
  result.initialFieldValuesFilter = profile.fieldValuesFilter;

  return result;
};

export const getFinalTableFilterData = (
  finalTableProps: FinalTableProps<any, any, any>
): FinalTableFilterData => {
  return {
    visibleFields: finalTableProps.filterVisibleFields,
  };
};

export const getFinalTableFilterDataKey = (
  finalTableProps: FinalTableProps<any, any, any>
): any[] => {
  return [finalTableProps?.filterVisibleFields];
};

export const getFinalTableProfileDataKey = (
  finalTableProps: FinalTableProps<any, any, any>
): any[] => {
  return [
    finalTableProps?.sort,
    finalTableProps?.showFooter,
    finalTableProps?.grouping,
    finalTableProps?.aggregate,
    finalTableProps?.columns,
    finalTableProps?.groupColumnWidth,
    finalTableProps?.fieldValuesFilter,
  ];
};

export const getFinalTableProfileData = (
  finalTableProps: FinalTableProps<any, any, any>
): FinalTableProfileData => {
  return {
    sort: finalTableProps?.sort,
    showFooter: finalTableProps?.showFooter,
    grouping: finalTableProps?.grouping,
    aggregate: finalTableProps?.aggregate,
    columns: finalTableProps?.columns,
    groupColumnWidth: finalTableProps.groupColumnWidth,
    fieldValuesFilter: finalTableProps.fieldValuesFilter,
  };
};

export const useTableSettings = (
  settingsKey?: string
): [FinalTableSettings, (data: FinalTableSettings) => void] => {
  if (!settingsKey) {
    return [{}, () => {}];
  }

  const [tableSettings, setTableSettings] = useNastaveni<FinalTableSettings>(
    `${GLOBAL_SAVED_SETTINGS_KEY}.${settingsKey}`,
    { profileData: undefined, filterData: undefined }
  );

  return [tableSettings, setTableSettings];
};

export const useTableProfiles = (
  settingsKey?: string
): [FinalTableProfiles, (data: FinalTableProfiles) => void] => {
  if (!settingsKey) {
    return [{ profiles: [] }, () => {}];
  }

  const [profiles, setProfiles] = useNastaveni<FinalTableProfiles>(
    `${GLOBAL_SAVED_PROFILES_KEY}.${settingsKey}`,
    { profiles: [] }
  );

  return [profiles, setProfiles];
};

export const useTableViews = (
  settingsKey?: string
): [FinalTableViews, (data: FinalTableViews) => void] => {
  if (!settingsKey) {
    return [{ views: [] }, () => {}];
  }

  const [views, setViews] = useNastaveni<FinalTableViews>(
    `${GLOBAL_SAVED_VIEWS_KEY}.${settingsKey}`,
    { views: [] }
  );

  return [views, setViews];
};

export const TableSettings = <
  DATA extends IData<ID>,
  FILTER extends unknown,
  ID extends unknown,
>({
  finalTableProps,
  settingsKey,
}: Props<DATA, FILTER, ID>) => {
  const [profiles, setProfiles] = useTableProfiles(settingsKey);
  const [views] = useTableViews(settingsKey);

  const handleTableColumns = () => {
    let columnsView = getColumnsView(
      finalTableProps.grouping,
      finalTableProps.columns,
      finalTableProps.config
    );
    let allColumns = finalTableProps.config.columns;
    const visibleColumns = getVisibleColumns(columnsView);

    /**
     * Nastavení sloupců - pořadí a viditelnosti.
     * @param visileColumns seznam fieldů (ident), které mají být viditelné včetně pořadí ja se mají zobrazit
     */
    const handleSetColumns = (visileColumns: string[]) => {
      const resultColumns = finalTableProps.columns.map((x) => ({
        ...x,
        visible: false,
      }));

      // Seřazení resultColumns dle předaných viditelných id, případně přidání do resultColumns, když tam není
      visileColumns.reverse().forEach((id) => {
        const index = resultColumns.findIndex((x) => x.ident === id);
        if (index >= 0) {
          resultColumns[index].visible = true;
          // Přesun na začátek
          resultColumns.unshift(resultColumns.splice(index, 1)[0]);
        } else {
          // Vložení nové položky na začátek
          resultColumns.unshift({
            ident: id,
            visible: true,
            // width: 200,
          });
        }
      });

      // Zrušit filtry sloupců, které jsou nyní skryté a mají nastavení v fieldValuesFilter
      const hiddenColumnIdents: string[] = [];
      allColumns.filter((item) => {
        let field = getColumnFieldNoCheck(item);
        if (field) {
          const isVisible =
            resultColumns.findIndex((x) => x.ident === field && x.visible) !==
            -1;
          if (!isVisible) {
            hiddenColumnIdents.push(field);
          }
        }
      });

      // Pokud pro nějaký skrytý sloupec existuje filtr na sloupci, tento filtr odebereme
      const currentFieldValuesFilter = finalTableProps.fieldValuesFilter || {};
      if (hiddenColumnIdents.some((ident) => currentFieldValuesFilter[ident])) {
        const newFieldValuesFilter = { ...currentFieldValuesFilter };
        hiddenColumnIdents.forEach((field) => {
          delete newFieldValuesFilter[field];
        });
        finalTableProps.setFieldValuesFilter(newFieldValuesFilter);
      }

      finalTableProps.setColumns(resultColumns);
    };

    const groupedIds = finalTableProps.grouping.map((x) => x.field);

    Modal.show(({ onClose }) => (
      <TableColumnsModal<DATA, ID>
        groupedColumns={groupedIds}
        onClose={onClose}
        allColumns={allColumns}
        visibleColumns={visibleColumns}
        setVisibleColumns={handleSetColumns}
      />
    ));
  };

  const onProfile = (profile?: FinalTableProfileType) => {
    finalTableProps.setProfile(profile?.profile);

    setProfiles({
      profiles: profiles.profiles,
      activeProfile: profile,
    });
  };

  const saveProfil = useCallback(() => {
    Modal.show(({ onClose }) => (
      <SaveByNameModalForm
        type={TableSettingsEnumType.PROFIL}
        onClose={onClose}
        onSubmit={async (data) => {
          const duplicitProfil = profiles.profiles.find(
            (item) => item.nazev === data.nazev
          );
          if (duplicitProfil) {
            const allowSubmit = await showConfirm({
              title: 'Profil s daným názvem již existuje',
              message: 'Přejete si jej nahradit?',
            });
            if (allowSubmit) {
              updateProfile(duplicitProfil);
              onClose();
            }
          } else {
            const activeProfile: FinalTableProfileType = {
              id: uuidv4(),
              nazev: data.nazev,
              profile: getFinalTableProfileData(finalTableProps),
            };
            setProfiles({
              profiles: [...profiles.profiles, activeProfile],
              activeProfile: activeProfile,
            });
            onClose();
          }
        }}
      />
    ));
  }, [
    profiles.profiles,
    setProfiles,
    finalTableProps?.sort,
    finalTableProps?.showFooter,
    finalTableProps?.grouping,
    finalTableProps?.aggregate,
    finalTableProps?.columns,
    finalTableProps?.fieldValuesFilter,
  ]);

  const removeProfile = async (profile: FinalTableProfileType) => {
    const permitRemove = await showDeleteConfirm({
      message: 'Smazat profil?',
    });
    const viewsUsingProfile = views.views.filter(
      (view) => view.view.profile === profile.id
    );
    if (permitRemove) {
      if (viewsUsingProfile.length > 0) {
        showInfo({
          title: 'Profil nelze smazat, je využívaný pohledem',
          message: `Profil je využíván těmito pohledy: ${viewsUsingProfile
            .map((view) => view.nazev)
            .join(', ')}`,
        });
        return;
      } else {
        setProfiles({
          profiles: profiles.profiles.filter(
            (item) => item.nazev !== profile.nazev
          ),
          activeProfile: undefined,
        });
      }
    }
  };
  const updateProfile = (profile: FinalTableProfileType) => {
    const updatedProfile: FinalTableProfileType = {
      id: profile.id,
      nazev: profile.nazev,
      profile: getFinalTableProfileData(finalTableProps),
    };
    setProfiles({
      profiles: [
        ...profiles.profiles.filter(
          (item) => item.nazev !== updatedProfile.nazev
        ),
        updatedProfile,
      ],
      activeProfile: updatedProfile,
    });
  };

  // --------------
  const currentProfileData: FinalTableProfileData = {
    sort: finalTableProps?.sort,
    showFooter: finalTableProps?.showFooter,
    grouping: finalTableProps?.grouping,
    aggregate: finalTableProps?.aggregate,
    columns: finalTableProps?.columns,
    groupColumnWidth: finalTableProps.groupColumnWidth,
    fieldValuesFilter: finalTableProps.fieldValuesFilter,
  };
  const activeProfileData = { ...profiles.activeProfile?.profile };
  if (!currentProfileData.fieldValuesFilter) {
    // kvůli kompatibilní změně kdy se přidává fieldValuesFilter a ten má nevyplněný hodnotu {}
    currentProfileData.fieldValuesFilter = {};
  }
  if (!activeProfileData.fieldValuesFilter) {
    // kvůli kompatibilní změně kdy se přidává fieldValuesFilter a ten má nevyplněný hodnotu {}
    activeProfileData.fieldValuesFilter = {};
  }
  // console.log('CURRENT', currentProfileData, 'ACTIVE', activeProfileData);
  const profileIsEquals =
    profiles.activeProfile && isEqual(activeProfileData, currentProfileData);

  // --------------

  const haveAnyAggregation = finalTableProps?.config?.columns?.some(
    (item) => item?.type && supportsAggregate(item.type)
  );
  return (
    <>
      {profiles.activeProfile && (
        <Tag
          title={`Profil ${profiles.activeProfile?.nazev}`}
          menu={
            <Menu>
              <MenuItem
                text={'Aktualizovat profil'}
                disabled={profileIsEquals}
                onClick={() =>
                  profiles.activeProfile &&
                  updateProfile(profiles.activeProfile)
                }
              />
              <MenuItem
                text={'Odstranit profil'}
                intent={'danger'}
                onClick={() =>
                  profiles.activeProfile &&
                  removeProfile(profiles.activeProfile)
                }
              />
            </Menu>
          }
        >
          {profiles.activeProfile?.nazev}
          {!profileIsEquals && <span> *</span>}
        </Tag>
      )}
      <Button
        style={'link'}
        icon={<Icon icon={mcSettings} />}
        menu={
          <Menu>
            <MenuDivider title={'Vzhled seznamu'} />
            <MenuItem
              text={'Vybrat sloupce'}
              icon={<Icon fixedWidth icon={mcSettings} />}
              onClick={handleTableColumns}
            />
            {!finalTableProps?.config?.disableFooter && (
              <MenuItem
                text={
                  finalTableProps.showFooter
                    ? 'Skrýt patičku'
                    : 'Zobrazit patičku'
                }
                icon={<Icon fixedWidth icon={mcShowFooter} />}
                onClick={() => {
                  finalTableProps.setShowFooter(!finalTableProps.showFooter);
                }}
                disabled={!haveAnyAggregation}
              />
            )}
            {settingsKey && (
              <>
                <MenuDivider title={'Profily'} />
                <MenuItem
                  text={'Výchozí'}
                  icon={
                    !profiles.activeProfile && (
                      <Icon fixedWidth icon={mcCheck} />
                    )
                  }
                  onClick={() => onProfile(undefined)}
                />
                {profiles.profiles.map((profile) => (
                  <MenuItem
                    icon={
                      isEqual(profiles.activeProfile, profile) && (
                        <Icon fixedWidth icon={mcCheck} />
                      )
                    }
                    text={profile.nazev}
                    onClick={() => onProfile(profile)}
                  />
                ))}
                <MenuItem
                  text={'Uložit jako nový profil…'}
                  icon={<Icon icon={mcSave} />}
                  onClick={() => {
                    saveProfil();
                  }}
                />
              </>
            )}
          </Menu>
        }
      />
    </>
  );
};
